import ENV from "config/Env";
import { backendQueryKey } from "hooks";

const component = "ROB";

export type ROBContracts = {
  leaseContract: LeaseContract;
  supplierContract: SupplierContract;
  lessor: ContractLessor;
  vehicle: ContractVehicle;
};

type LeaseContract = {
  endDate: string;
  maximumMileage: number;
  repairAndMaintenance: LeaseContractRepairAndMaintenance;
  tires: LeaseContractTires;
  rentalVehicle: LeaseContractRentalVehicle;
};

type LeaseContractRepairAndMaintenance = {
  inContract: boolean;
  remark: string;
};

type LeaseContractTires = {
  inContract: string;
  tireSwapInContract: string;
  rofTiresInContract: string;
  allSeasonTiresInContract: string;
  nitrogenTireFillingInContract: boolean;
  usedTiresInContract: boolean;
  hasTireSetForTireSwapInStorage: boolean;
};

type LeaseContractRentalVehicle = {
  inContract: boolean;
  remark: string;
  rentalHoursChargedToLesseeForRepairAndMaintenance?: number;
  rentalHoursChargedToLesseeForDamage?: number;
  rentalClass?: string;
  fuelType: string;
  surchargeEstate: boolean;
  surchargeAutomaticTransmission: boolean;
  rentalClassesInContract: RentalClass[];
  rentalClassesNotInContract: RentalClass[];
};

type RentalClass = {
  rentalClass: string;
  fuelType: string;
  forEstate: boolean;
  freeKilometrage: number;
};

type SupplierContract = {
  repairAndMaintenance: SupplierContractRepairAndMaintenance;
  tires: SupplierContractTires;
  rentalVehicle: SupplierContractRentalVehicle;
};

type SupplierContractRepairAndMaintenance = {
  allowed: boolean;
  componentsWithDamageAllowed: boolean;
};

type SupplierContractTires = {
  tireSwapAllowed: boolean;
  tireReplaceAllowed: boolean;
  tirePurchaseWithoutMountingAllowed: boolean;
  usedTiresAllowed: boolean;
  profileDepthRequiredExemption: boolean;
  allowedCosts: SupplierContractTiresAllowedCosts;
};

type SupplierContractTiresAllowedCosts = {
  tireStorageCosts: boolean;
  tireTransportCosts: boolean;
  surchargeNitrogen: boolean;
  surchargeRunOnFlat: boolean;
  surchargeLargeRim: boolean;
  surchargeMobileService: boolean;
  surchargeTruckTire: boolean;
  handlingCostLessorStock: boolean;
  handlingCostThirdPartyTire: boolean;
  tireDisposalFee: boolean;
  deliveryFeeToDealer: boolean;
  tireTrim: boolean;
  tirematch: boolean;
  tireRegroove: boolean;
  wheelBalance: boolean;
  wheelExchange: boolean;
};

type SupplierContractRentalVehicle = {
  allowed: boolean;
  allowedCosts: SupplierContractRentalVehicleAllowedCosts;
};

type SupplierContractRentalVehicleAllowedCosts = {
  deliveryCosts: boolean;
  pickupCosts: boolean;
  fuelServiceCosts: boolean;
};

type ContractLessor = {
  number: number;
  name: string;
  subLessorCode: string;
};

type ContractVehicle = {
  vehicleMake: string;
  vehicleModel: string;
};

export type ROBSlas = {
  default: SlaSections;
  agreements: SlaAgreement[];
};

export type SlaSections = {
  repairAndMaintenance: SlaRepairAndMaintenance;
  tires: SlaTires;
  rentalVehicle: SlaRentalVehicle;
};

type SlaRepairAndMaintenance = {
  rates: SlaRates;
};

type SlaRates = {
  current?: SlaRatesCurrent;
};

type SlaRatesCurrent = {
  startDate: string;
  values: SlaRateValue[];
};

export type SlaRateValue = {
  robCode: string;
  price: number;
  operationCode?: string;
  objectTypeCategory?: string;
  rob_code_description: string;
  operation_code_description: string;
};

type SlaTires = {
  rates: SlaRates;
  preferredMakes: SlaPreferredMakes;
  purchaseDiscounts: Record<string, unknown>;
};

type SlaPreferredMakes = {
  current?: SlaPreferredMakesCurrent;
};

type SlaPreferredMakesCurrent = {
  startDate: string;
  values: SlaPreferredMake[];
};

type SlaPreferredMake = {
  ordinal: number;
  tireMake: SlaTireMake;
};

type SlaTireMake = {
  code: string;
  name: string;
};

type SlaRentalVehicle = {
  rates: SlaRentalVehicleRates;
};

type SlaRentalVehicleRates = {
  current?: SlaRentalVehicleRatesCurrent;
};

type SlaRentalVehicleRatesCurrent = {
  startDate: string;
  rentalClasses: SlaRentalClass[];
};

export type SlaRentalClass = {
  rentalClass: string;
  fuelType: string;
  forEstate: boolean;
  dailyRate: number;
  freeKilometrage: number;
  kilometrageSurchargeRate: number;
  automaticTransmissionSurchargeRate: number;
};

export type SlaAgreement = {
  id: number;
  name: string;
  isClusterAgreement: boolean;
  status: string;
  repairAndMaintenance: SlaRepairAndMaintenance;
  tires: SlaTires;
  rentalVehicle: SlaRentalVehicle;
  lessors: SlaLessor[];
};

type SlaLessor = {
  id: number;
  name: string;
};

type ROBServiceRequestResponse = {
  id: string;
  serviceRequestNumber: number;
  creationTimeStamp: string;
  status: string;
  readOnly: boolean;
  supplierId: number;
  lessorId: number;
  licensePlate: string;
  parking: ServiceRequestParking;
  remarks: ServiceRequestRemarks;
  appointment: ServiceRequestAppointment;
  components: ServiceRequestComponent[];
  invoicing: ServiceRequestInvoicing;
  serviceRequestURL: string;
};

type ServiceRequestParking = {
  isParked: boolean;
  remark: string;
};

type ServiceRequestRemarks = {
  supplier: string;
  lessor: string;
};

type ServiceRequestAppointment = {
  workshopDate: string;
  workOrderNumber: string;
  contact: ServiceRequestContact;
  estimatedDurationInDays: number;
};

type ServiceRequestContact = {
  name: string;
  phone: string;
};

export type ServiceRequestComponent = {
  id: string;
  rob: ServiceRequestCode;
  supplierCode: string;
  operation: ServiceRequestCode;
  reason: ServiceRequestCode;
  status: ServiceRequestCode;
  price: number;
  location?: string[];
  partCount?: number;
  denialReasons?: ServiceRequestDenialReason[];
  value?: string;
};

type ServiceRequestCode = {
  code: string;
  description: string;
};

type ServiceRequestDenialReason = {
  lessorRemark: string;
  code: string;
};

type ServiceRequestInvoicing = {
  status: string;
  totalAmountApproved?: number;
  totalAmountToInvoice?: number;
  totalAmountInvoiced?: number;
};

export const ROBKeys = {
  contracts: (car_id: number) => backendQueryKey<ROBContracts | null>({ baseUrl: ENV.carBaseURL, component, endpoint: "/robnet/get_contract", params: { car_id } }),
  slas: (car_id: number) => backendQueryKey<ROBSlas | null>({ baseUrl: ENV.carBaseURL, component, endpoint: "/robnet/get_slas", params: { car_id } }),
  serviceRequest: (car_id: number) =>
    backendQueryKey<ROBServiceRequestResponse | null>({ baseUrl: ENV.carBaseURL, component, endpoint: "/robnet/service_requests/list", params: { car_id } })
};
